import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import type { CeldaHorario } from '@/types/reports';
import {
  DIAS_SEMANA_CORTOS,
  etiquetaCelda,
  formatCount,
  intensidadCelda,
  maximoMatriz,
  valorCelda,
  type CampoHorario,
} from '../reports-format';

/**
 * Mapa de calor de actividad (7 días × 24 horas) en la zona del NEGOCIO.
 *
 * Es CSS puro, no recharts: son 168 celdas de un color y una rejilla de ejes; con una librería de
 * gráficas esto sería más código, más peso y menos legible. El color va en ESCALONES (no continuo)
 * porque el ojo no distingue 168 tonos: con cinco niveles se ve de un vistazo cuándo entra el
 * trabajo, y el número exacto de cada celda está en el `title` (tooltip del navegador).
 *
 * El mapa SIEMPRE se dibuja con las 24 horas: recortar las horas vacías parece un detalle, pero
 * movería las columnas de sitio justo cuando cambia el rango y haría incomparables dos capturas.
 */

/** Escala del mapa: del gris «sin datos» al azul fuerte de la marca (mismo orden que los tokens). */
const NIVELES = [
  'bg-surface-muted',
  'bg-brand-100',
  'bg-brand-200',
  'bg-brand-400',
  'bg-brand-600',
] as const;

/** Nivel de color de una celda (0 = sin actividad). */
function nivelDeIntensidad(intensidad: number): string {
  if (intensidad <= 0) return NIVELES[0];
  if (intensidad < 0.25) return NIVELES[1];
  if (intensidad < 0.5) return NIVELES[2];
  if (intensidad < 0.75) return NIVELES[3];
  return NIVELES[4];
}

export interface HeatmapGridProps {
  /** La matriz 7×24 tal como la manda el backend (sin reordenar: cada celda trae su día y su hora). */
  matriz: CeldaHorario[][];
  /** Qué se intensifica: cuándo escribe el cliente (`mensajes`) o cuándo entra el lead. */
  campo: CampoHorario;
  /** Nota al pie (ej. la frase del pico: ver `describirPico`). */
  footer?: ReactNode;
  className?: string;
}

export function HeatmapGrid({ matriz, campo, footer, className }: HeatmapGridProps) {
  if (matriz.length === 0) {
    return (
      <p className={cn('text-caption text-ink-muted', className)}>
        El backend no devolvió el mapa de horarios de este periodo.
      </p>
    );
  }

  const maximo = maximoMatriz(matriz, campo);
  const unidad = campo === 'mensajes' ? 'mensajes' : 'conversaciones iniciadas';

  return (
    <div className={className}>
      {/* En móvil el mapa scrollea en horizontal: 24 columnas legibles no caben en 360px. */}
      <div className="scroll-thin overflow-x-auto">
        <div className="min-w-[560px]">
          <div className="flex items-center gap-1 pb-1">
            <span className="w-10 shrink-0" aria-hidden="true" />

            {matriz[0].map((celda) => (
              <span
                key={celda.hora}
                className="tabular flex-1 text-center text-micro text-ink-faint"
              >
                {String(celda.hora).padStart(2, '0')}
              </span>
            ))}
          </div>

          {matriz.map((fila, indice) => (
            <div key={fila[0]?.dia ?? indice} className="flex items-center gap-1 pb-1">
              <span className="w-10 shrink-0 text-micro text-ink-muted">
                {DIAS_SEMANA_CORTOS[fila[0]?.dia ?? indice] ?? `Día ${indice}`}
              </span>

              {fila.map((celda) => {
                const valor = valorCelda(celda, campo);

                return (
                  <span
                    key={celda.hora}
                    title={`${etiquetaCelda(celda)}: ${formatCount(valor)} ${unidad}`}
                    className={cn(
                      'h-6 flex-1 rounded-[4px]',
                      nivelDeIntensidad(intensidadCelda(valor, maximo)),
                    )}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-micro text-ink-faint">
        <span className="flex items-center gap-1">
          Menos
          {NIVELES.map((nivel) => (
            <span key={nivel} className={cn('size-3 rounded-[3px]', nivel)} aria-hidden="true" />
          ))}
          Más
        </span>

        <span className="tabular">
          {maximo > 0
            ? `La hora más movida del periodo tiene ${formatCount(maximo)} ${unidad}`
            : 'Ninguna hora del periodo tuvo actividad'}
        </span>
      </div>

      {footer ? <p className="mt-2 text-caption text-ink-muted">{footer}</p> : null}
    </div>
  );
}
